import { useCallback, useState } from 'react';
import type { CreateReportDialogTrigger } from './create-report-dialog-selection';
import {
  collectLoadedFindingsById,
  selectionIncludesUnpublishedFindings,
  type FindingPublication,
  type LoadedFindingPublication,
  type ReportSelectionForUnpublishedCheck,
} from './selection-includes-unpublished-findings';

type UseReportUnpublishedConfirmArgs = {
  selection: ReportSelectionForUnpublishedCheck;
  loadedFindings: readonly LoadedFindingPublication[];
  openReportDialog: (trigger: CreateReportDialogTrigger) => void;
};

type UseReportUnpublishedConfirmResult = {
  isConfirmOpen: boolean;
  openWithConfirm: (args: {
    trigger: CreateReportDialogTrigger;
    rowFinding?: FindingPublication;
  }) => void;
  confirmPending: () => void;
  cancelPending: () => void;
};

export function useReportUnpublishedConfirm({
  selection,
  loadedFindings,
  openReportDialog,
}: UseReportUnpublishedConfirmArgs): UseReportUnpublishedConfirmResult {
  const [pendingTrigger, setPendingTrigger] =
    useState<CreateReportDialogTrigger | null>(null);

  const openWithConfirm = useCallback(
    ({
      trigger,
      rowFinding,
    }: {
      trigger: CreateReportDialogTrigger;
      rowFinding?: FindingPublication;
    }) => {
      const includesUnpublished = selectionIncludesUnpublishedFindings({
        selection,
        loadedFindingsById: collectLoadedFindingsById(loadedFindings),
        rowFinding,
      });

      if (includesUnpublished) {
        setPendingTrigger(trigger);

        return;
      }

      openReportDialog(trigger);
    },
    [selection, loadedFindings, openReportDialog],
  );

  const confirmPending = useCallback(() => {
    if (!pendingTrigger) return;

    openReportDialog(pendingTrigger);
    setPendingTrigger(null);
  }, [pendingTrigger, openReportDialog]);

  const cancelPending = useCallback(() => {
    setPendingTrigger(null);
  }, []);

  return {
    isConfirmOpen: pendingTrigger !== null,
    openWithConfirm,
    confirmPending,
    cancelPending,
  };
}
